"use client";

import { useState, useMemo } from "react";
import {
  Badge,
  Group,
  Stack,
  SegmentedControl,
  SimpleSearch,
} from "@forestsync/ui";
import { useTranslations } from "next-intl";
import { CarbonSinkList } from "./CarbonSinkList";
import { ProjectDeveloperTable } from "./ProjectDeveloperTable";
import { BeneficiaryTable } from "./BeneficiaryTable";
import type { CarbonSink, User } from "@/types/glassroom";
import { getCountryCode, getCountryName } from "@forestsync/db";

interface GlassroomTabsProps {
  carbonSinks: CarbonSink[];
  projectDevelopers: User[];
  beneficiaries: User[];
}

export function GlassroomTabs({
  carbonSinks,
  projectDevelopers,
  beneficiaries,
}: GlassroomTabsProps) {
  const t = useTranslations();
  const [activeTab, setActiveTab] = useState<string>("carbonSinks");
  const [searchTerm, setSearchTerm] = useState<string>("");

  const applySearch = (data: (CarbonSink | User)[], search: string) => {
    if (!search.trim()) return data;

    const searchLower = search.toLowerCase();
    return data.filter((item) => {
      const searchableFields = [
        item.name?.toLowerCase(),
        item.refId?.toLowerCase(),
        item.country ? getCountryName(getCountryCode(item.country)).toLowerCase() : undefined,
        item.region?.toLowerCase(),
        item.municipality?.toLowerCase(),
      ];

      if ("ownerName" in item) {
        searchableFields.push(item.ownerName?.toLowerCase());
      }

      return searchableFields.some((field) => field?.includes(searchLower));
    });
  };

  const currentData = useMemo(() => {
    return activeTab === "carbonSinks"
      ? carbonSinks
      : activeTab === "projectDevelopers"
        ? projectDevelopers
        : beneficiaries;
  }, [activeTab, carbonSinks, projectDevelopers, beneficiaries]);

  const filteredCurrentData = useMemo(() => {
    return applySearch(currentData, searchTerm);
  }, [currentData, searchTerm]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const tabOptions = [
    { label: t("glassroom.tabs.carbonSinks"), value: "carbonSinks" },
    {
      label: t("glassroom.tabs.projectDevelopers"),
      value: "projectDevelopers",
    },
    { label: t("glassroom.tabs.beneficiaries"), value: "beneficiaries" },
  ];

  return (
    <Stack gap="lg">
      <SegmentedControl
        value={activeTab}
        onChange={setActiveTab}
        data={tabOptions}
        size="lg"
        radius="md"
        visibleFrom="sm"
        withItemsBorders={false}
      />
      <SegmentedControl
        value={activeTab}
        onChange={setActiveTab}
        data={tabOptions}
        size="xs"
        radius="md"
        hiddenFrom="sm"
        withItemsBorders={false}
        fullWidth
        py="0.5rem"
        classNames={{
          label: "py-2",
        }}
      />

      <SimpleSearch
        value={searchTerm}
        onSearch={handleSearch}
        placeholder={t("glassroom.search.placeholder")}
      />

      <Group justify="flex-end">
        {filteredCurrentData.length > 0 && (
          <Badge variant="filled" color="blue" size="sm">
            {activeTab === "carbonSinks"
              ? t("common.forest", { count: filteredCurrentData.length })
              : activeTab === "projectDevelopers"
                ? t("common.developers", { count: filteredCurrentData.length })
                : t("common.beneficiaries", {
                    count: filteredCurrentData.length,
                  })}
          </Badge>
        )}
      </Group>

      {activeTab === "carbonSinks" ? (
        <CarbonSinkList sinks={filteredCurrentData as CarbonSink[]} />
      ) : activeTab === "projectDevelopers" ? (
        <ProjectDeveloperTable developers={filteredCurrentData as User[]} />
      ) : (
        <BeneficiaryTable beneficiaries={filteredCurrentData as User[]} />
      )}

      {filteredCurrentData.length === 0 && searchTerm && (
        <div className="text-center p-8">
          <Badge variant="light" color="gray" size="lg">
            {t("glassroom.search.noResults")}
          </Badge>
        </div>
      )}
    </Stack>
  );
}
